import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Building2,
  Phone,
  Mail,
  MapPin,
  Plus,
  Edit,
  Trash2,
  X,
  Loader2,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Download,
  Upload,
  ClipboardList
} from 'lucide-react';
import {
  getBusinessContacts,
  createBusinessContact,
  updateBusinessContact,
  deleteBusinessContact,
  extractBusinessContactDataFromText,
  importBusinessContactsFromCSV
} from '../../../redux/api';
import { COUNTRY_DATA } from '../../../utils/countryData';
import { buildBusinessContactsCsv } from '../../../utils/businessContactCsv';
import { buildGmailComposeUrl } from '../../../utils/gmailCompose';
import { buildOutreachTemplates } from '../../../utils/outreachEmailTemplates';
import EmailProviderInput from '../../ui/EmailProviderInput';

const BusinessContactsView = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [formData, setFormData] = useState({
    businessType: '',
    companyName: '',
    mobilePhone: '',
    fixedPhone: '',
    email: '',
    country: '',
    countryCode: 'rs', // Default to Serbia
    city: '',
    address: '',
    contactStatus: 'nije_kontaktiran',
    interestLevel: '',
    lastContactedAt: '',
    contactNotes: '',
    contactedVia: []
  });
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterContactStatus, setFilterContactStatus] = useState('');
  const [filterHasEmail, setFilterHasEmail] = useState(false);
  const [filterScoreChannel, setFilterScoreChannel] = useState('');
  const [filterMinScore, setFilterMinScore] = useState('');
  const [showLogContactModal, setShowLogContactModal] = useState(false);
  const [logContactTarget, setLogContactTarget] = useState(null);
  const [logContactForm, setLogContactForm] = useState({
    contactStatus: 'kontaktiran',
    interestLevel: '',
    lastContactedAt: new Date().toISOString().slice(0, 10),
    contactNotes: '',
    contactedVia: [],
    viberScore: '',
    whatsappScore: '',
    emailScore: '',
    telefonScore: '',
    packageLevel: ''
  });
  const [savingLog, setSavingLog] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiText, setAiText] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalContacts, setTotalContacts] = useState(0);
  const [allContacts, setAllContacts] = useState([]); // Store all contacts when "Samo mejlovi" filter is active
  const [loadingAllContacts, setLoadingAllContacts] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [pageInput, setPageInput] = useState('1');
  const [focusContactId, setFocusContactId] = useState(null);

  const { user } = useSelector((state) => state.auth);
  const isSuperAdmin = user?.result?.role === 'superadmin';

  useEffect(() => {
    if (!isSuperAdmin) {
      toast.error('Nemate pristup ovoj stranici');
      return;
    }
    fetchAllContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  // Pri promeni filtera uvek kreni od prve stranice filtriranog skupa
  useEffect(() => {
    setCurrentPage(1);
  }, [
    filterCountry,
    filterCity,
    filterContactStatus,
    filterHasEmail,
    filterScoreChannel,
    filterMinScore,
    searchTerm
  ]);

  const fetchAllContacts = async () => {
    try {
      setLoading(true);
      setLoadingAllContacts(true);
      const response = await getBusinessContacts(1, 10000);
      const data = response.data || {};
      const list = data.contacts || [];
      setAllContacts(list);
      setContacts(list);
      setTotalContacts(data.total ?? list.length);
      setTotalPages(Math.max(1, Math.ceil(list.length / 10)));
    } catch (error) {
      console.error('Error fetching all contacts:', error);
      toast.error('Greška pri učitavanju kontakata');
    } finally {
      setLoading(false);
      setLoadingAllContacts(false);
    }
  };

  const replaceContactInLists = (updated) => {
    if (!updated?._id) return;
    const id = String(updated._id);
    const merge = (list) => list.map((contact) => (
      String(contact._id) === id ? { ...contact, ...updated, _id: contact._id } : contact
    ));
    setAllContacts(merge);
    setContacts(merge);
  };

  const removeContactFromLists = (id) => {
    const sid = String(id);
    setAllContacts((list) => list.filter((contact) => String(contact._id) !== sid));
    setContacts((list) => list.filter((contact) => String(contact._id) !== sid));
    setTotalContacts((count) => Math.max(0, count - 1));
  };

  // Helper function to extract subscriber number from E.164 format
  const extractSubscriberNumber = (phone) => {
    if (!phone || !phone.startsWith('+')) return null;
    
    const numberOnly = phone.substring(1);
    
    // Known country codes (sorted by length, longest first to avoid partial matches)
    // Include all codes from COUNTRY_DATA
    const knownCodes = Object.values(COUNTRY_DATA)
      .map(data => data.code)
      .sort((a, b) => b.length - a.length);
    
    // Find the longest matching country code to avoid partial matches
    // Example: +381631615749 should match "381" not "38"
    let bestMatch = { code: '', subscriber: numberOnly, length: 0 };
    
    for (const code of knownCodes) {
      if (numberOnly.startsWith(code) && numberOnly.length >= code.length + 6) {
        const extracted = numberOnly.substring(code.length);
        
        // Validate: extracted should be 6-12 digits (typical subscriber number length)
        const cleanExtracted = extracted.startsWith('0') ? extracted.substring(1) : extracted;
        
        if (cleanExtracted.length >= 6 && cleanExtracted.length <= 12 && /^\d+$/.test(cleanExtracted)) {
          // Prefer longer codes (more specific match)
          if (code.length > bestMatch.length) {
            bestMatch = { code, subscriber: extracted, length: code.length };
          }
        }
      }
    }
    
    if (bestMatch.length > 0) {
      console.log(`📞 Frontend: Extracted subscriber number from ${phone}: removed ${bestMatch.code}, got ${bestMatch.subscriber}`);
      return bestMatch.subscriber;
    }
    
    // If no match, try to extract last 8-10 digits (typical subscriber number length)
    if (numberOnly.length >= 10) {
      const extracted = numberOnly.substring(numberOnly.length - 10);
      console.log(`📞 Frontend: Could not detect country code, extracted last 10 digits: ${extracted}`);
      return extracted;
    } else if (numberOnly.length >= 8) {
      const extracted = numberOnly.substring(numberOnly.length - 8);
      console.log(`📞 Frontend: Could not detect country code, extracted last 8 digits: ${extracted}`);
      return extracted;
    }
    
    console.log(`📞 Frontend: Number too short, returning as is: ${numberOnly}`);
    return numberOnly;
  };

  // Helper function to normalize phone with new country code
  const renormalizePhone = (phone, newCountryCode) => {
    if (!phone) return '';
    
    console.log(`📞 Frontend re-normalizing: phone=${phone}, newCountryCode=${newCountryCode}`);
    
    const subscriberNumber = extractSubscriberNumber(phone);
    if (!subscriberNumber) {
      console.log(`📞 Frontend: Could not extract subscriber number, returning original`);
      return phone; // Return original if can't extract
    }
    
    console.log(`📞 Frontend: Extracted subscriber number: ${subscriberNumber}`);
    
    // Remove leading 0 if present
    let cleanNumber = subscriberNumber.startsWith('0') ? subscriberNumber.substring(1) : subscriberNumber;
    
    // Get new dialing code
    const newDialingCode = COUNTRY_DATA[newCountryCode]?.code || '381';
    
    console.log(`📞 Frontend: newDialingCode=${newDialingCode}, cleanNumber=${cleanNumber}`);
    
    // Construct new E.164 format
    const result = `+${newDialingCode}${cleanNumber}`;
    console.log(`📞 Frontend: Final result: ${result}`);
    
    return result;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // If country code changes, auto-fill country name and re-normalize phones
    if (name === 'countryCode' && value) {
      const countryData = COUNTRY_DATA[value];
      if (countryData) {
        setFormData(prev => {
          const newData = {
            ...prev,
            countryCode: value,
            country: countryData.name
          };
          
          // Re-normalize phone numbers with new country code
          if (prev.mobilePhone) {
            newData.mobilePhone = renormalizePhone(prev.mobilePhone, value);
          }
          if (prev.fixedPhone) {
            newData.fixedPhone = renormalizePhone(prev.fixedPhone, value);
          }
          
          return newData;
        });
        return;
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      businessType: '',
      companyName: '',
      mobilePhone: '',
      fixedPhone: '',
      email: '',
      country: '',
      countryCode: 'rs',
      city: '',
      address: '',
      contactStatus: 'nije_kontaktiran',
      interestLevel: '',
      lastContactedAt: '',
      contactNotes: '',
      contactedVia: []
    });
    setEditingContact(null);
    setShowForm(false);
  };

  const CONTACT_STATUS_LABELS = {
    nije_kontaktiran: 'Nije kontaktiran',
    kontaktiran: 'Kontaktiran',
    zainteresovan: 'Zainteresovan',
    nije_zainteresovan: 'Nije zainteresovan',
    postao_klijent: 'Postao klijent'
  };

  const PACKAGE_LEVEL_LABELS = {
    1: 'Registrovan',
    2: 'Starter paket',
    3: 'Standard',
    4: 'Business',
    5: 'Premium'
  };

  const SCORE_CHANNEL_FIELDS = {
    viber: 'viberScore',
    whatsapp: 'whatsappScore',
    email: 'emailScore',
    telefon: 'telefonScore',
    paket: 'packageLevel'
  };

  const SCORE_CHANNEL_LABELS = {
    viber: 'Viber',
    whatsapp: 'WhatsApp',
    email: 'Mejl',
    telefon: 'Telefon',
    paket: 'Paket'
  };

  const getScoreOnChannel = (contact, channel) => {
    const field = SCORE_CHANNEL_FIELDS[channel];
    if (!field) return null;
    const raw = contact[field];
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return n >= 1 && n <= 5 ? n : null;
  };

  const contactMatchesScoreFilter = (contact) => {
    if (!filterMinScore) return true;
    const min = Number(filterMinScore);
    if (!Number.isFinite(min)) return true;

    if (filterScoreChannel) {
      const score = getScoreOnChannel(contact, filterScoreChannel);
      return score != null && score >= min;
    }

    return Object.keys(SCORE_CHANNEL_FIELDS).some((channel) => {
      const score = getScoreOnChannel(contact, channel);
      return score != null && score >= min;
    });
  };

  const hasActiveFilters = () =>
    !!(
      filterCountry ||
      filterCity ||
      filterContactStatus ||
      filterHasEmail ||
      filterCountry === 'samo-mejlovi' ||
      filterMinScore ||
      filterScoreChannel ||
      (searchTerm && searchTerm.trim())
    );

  // Badge-ovi za Status: prikazuju se samo kanali na koje je kontaktiran (ima ocenu 1–5) i paket ako je izabran
  const renderStatusBadges = (contact, small = false) => {
    const size = small ? 'w-3 h-3 text-[8px]' : 'w-4 h-4 text-[10px]';
    const allBadges = [
      { key: 'viber', score: contact.viberScore, className: 'bg-purple-600 border border-purple-500 text-white', title: 'Viber' },
      { key: 'whatsapp', score: contact.whatsappScore, className: 'bg-white border border-green-500 text-green-600', title: 'WhatsApp' },
      { key: 'email', score: contact.emailScore, className: 'bg-black border border-black text-white', title: 'Mejl' },
      { key: 'telefon', score: contact.telefonScore, className: 'bg-orange-500 border border-orange-600 text-white', title: 'Telefon' },
      { key: 'paket', score: contact.packageLevel, className: 'bg-red-600 border border-red-500 text-white', title: contact.packageLevel && contact.packageLevel >= 1 && contact.packageLevel <= 5 ? PACKAGE_LEVEL_LABELS[contact.packageLevel] : 'Paket' }
    ];
    const hasScore = (s) => s != null && s !== '' && Number(s) >= 1 && Number(s) <= 5;
    const badges = allBadges.filter(b => hasScore(b.score));
    if (badges.length === 0) return <span className="text-gray-500 text-xs">–</span>;
    return (
      <div className="flex items-center gap-0.5 flex-nowrap">
        {badges.map(({ key, score, className, title }) => (
          <span
            key={key}
            title={title + (key === 'paket' ? `: ${PACKAGE_LEVEL_LABELS[Number(score)]}` : `: ${score}`)}
            className={`inline-flex items-center justify-center font-semibold rounded ${size} border ${className} flex-shrink-0`}
          >
            {Number(score)}
          </span>
        ))}
      </div>
    );
  };

  const openLogContactModal = (contact) => {
    setLogContactTarget(contact);
    setLogContactForm({
      contactStatus: contact.contactStatus || 'kontaktiran',
      interestLevel: contact.interestLevel ?? '',
      lastContactedAt: (contact.lastContactedAt ? new Date(contact.lastContactedAt).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10)),
      contactNotes: contact.contactNotes || '',
      contactedVia: contact.contactedVia && Array.isArray(contact.contactedVia) ? [...contact.contactedVia] : [],
      viberScore: contact.viberScore != null && contact.viberScore !== '' ? Number(contact.viberScore) : '',
      whatsappScore: contact.whatsappScore != null && contact.whatsappScore !== '' ? Number(contact.whatsappScore) : '',
      emailScore: contact.emailScore != null && contact.emailScore !== '' ? Number(contact.emailScore) : '',
      telefonScore: contact.telefonScore != null && contact.telefonScore !== '' ? Number(contact.telefonScore) : '',
      packageLevel: contact.packageLevel != null && contact.packageLevel !== '' ? Number(contact.packageLevel) : ''
    });
    setShowLogContactModal(true);
  };

  const toggleContactedVia = (channel) => {
    setLogContactForm(prev => ({
      ...prev,
      contactedVia: prev.contactedVia.includes(channel)
        ? prev.contactedVia.filter(c => c !== channel)
        : [...prev.contactedVia, channel]
    }));
  };

  const handleSaveLogContact = async () => {
    if (!logContactTarget) return;
    try {
      setSavingLog(true);
      const countryData = COUNTRY_DATA[logContactTarget.countryCode] || COUNTRY_DATA['rs'];
      const response = await updateBusinessContact({
        id: logContactTarget._id,
        contactData: {
          ...logContactTarget,
          countryCode: logContactTarget.countryCode || 'rs',
          country: countryData.name,
          contactStatus: logContactForm.contactStatus,
          interestLevel: logContactForm.interestLevel !== '' && logContactForm.interestLevel != null ? Number(logContactForm.interestLevel) : undefined,
          lastContactedAt: logContactForm.lastContactedAt || undefined,
          contactNotes: logContactForm.contactNotes || '',
          contactedVia: logContactForm.contactedVia,
          viberScore: logContactForm.viberScore !== '' && logContactForm.viberScore != null ? Number(logContactForm.viberScore) : undefined,
          whatsappScore: logContactForm.whatsappScore !== '' && logContactForm.whatsappScore != null ? Number(logContactForm.whatsappScore) : undefined,
          emailScore: logContactForm.emailScore !== '' && logContactForm.emailScore != null ? Number(logContactForm.emailScore) : undefined,
          telefonScore: logContactForm.telefonScore !== '' && logContactForm.telefonScore != null ? Number(logContactForm.telefonScore) : undefined,
          packageLevel: logContactForm.packageLevel !== '' && logContactForm.packageLevel != null ? Number(logContactForm.packageLevel) : undefined
        }
      });
      toast.success('Evidencija kontakta sačuvana');
      replaceContactInLists(response.data);
      setFocusContactId(logContactTarget._id);
      setShowLogContactModal(false);
      setLogContactTarget(null);
    } catch (error) {
      console.error('Error saving contact log:', error);
      toast.error(error.response?.data?.message || 'Greška pri čuvanju');
    } finally {
      setSavingLog(false);
    }
  };

  // Helper function to detect country code from phone number
  const detectCountryCodeFromPhone = (phone) => {
    if (!phone) return 'rs';
    // If phone starts with +381, it's Serbia
    if (phone.startsWith('+381')) return 'rs';
    if (phone.startsWith('+387')) return 'ba';
    if (phone.startsWith('+385')) return 'hr';
    if (phone.startsWith('+382')) return 'me';
    if (phone.startsWith('+389')) return 'mk';
    if (phone.startsWith('+386')) return 'si';
    return 'rs'; // Default
  };

  const handleEdit = (contact) => {
    console.log('📝 Editing contact:', contact);
    console.log('📝 Contact countryCode:', contact.countryCode);
    
    setEditingContact(contact);
    // Use saved countryCode or try to detect from phone numbers
    let detectedCountryCode = contact.countryCode || 'rs';
    
    if (!contact.countryCode || contact.countryCode === 'rs') {
      // Try to detect from phone numbers
      const phoneBasedCode = detectCountryCodeFromPhone(contact.mobilePhone || contact.fixedPhone);
      if (phoneBasedCode && phoneBasedCode !== 'rs') {
        detectedCountryCode = phoneBasedCode;
        console.log('📝 Detected countryCode from phone:', detectedCountryCode);
      }
    }
    
    // If we have country name but no countryCode, try to match it
    if (contact.country && (!contact.countryCode || contact.countryCode === 'rs')) {
      const matchedCountry = Object.entries(COUNTRY_DATA).find(
        ([code, data]) => data.name.toLowerCase() === contact.country.toLowerCase()
      );
      if (matchedCountry) {
        detectedCountryCode = matchedCountry[0];
        console.log('📝 Matched countryCode from country name:', detectedCountryCode);
      }
    }
    
    console.log('📝 Final detectedCountryCode:', detectedCountryCode);
    
    setFormData({
      businessType: contact.businessType || '',
      companyName: contact.companyName || '',
      mobilePhone: contact.mobilePhone || '',
      fixedPhone: contact.fixedPhone || '',
      email: contact.email || '',
      country: contact.country || '',
      countryCode: detectedCountryCode,
      city: contact.city || '',
      address: contact.address || '',
      contactStatus: contact.contactStatus || 'nije_kontaktiran',
      interestLevel: contact.interestLevel ?? '',
      lastContactedAt: contact.lastContactedAt ? new Date(contact.lastContactedAt).toISOString().slice(0, 10) : '',
      contactNotes: contact.contactNotes || '',
      contactedVia: contact.contactedVia && Array.isArray(contact.contactedVia) ? [...contact.contactedVia] : []
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.companyName.trim()) {
      toast.error('Ime kompanije je obavezno');
      return;
    }

    try {
      setSubmitting(true);
      
      console.log('📤 Submitting form data:', formData);
      
      // Ensure countryCode is always sent, and set country based on countryCode
      const countryData = COUNTRY_DATA[formData.countryCode] || COUNTRY_DATA['rs'];
      const dataToSend = {
        ...formData,
        countryCode: formData.countryCode || 'rs', // Always include countryCode
        country: countryData.name // Auto-set country based on countryCode
      };
      // Za update eksplicitno šaljemo sva polja da backend sigurno primi (npr. email koji nije bio pri kreiranju)
      if (editingContact) {
        const updatePayload = {
          companyName: dataToSend.companyName,
          businessType: dataToSend.businessType ?? '',
          mobilePhone: dataToSend.mobilePhone ?? '',
          fixedPhone: dataToSend.fixedPhone ?? '',
          email: dataToSend.email ?? '',
          country: dataToSend.country ?? '',
          countryCode: dataToSend.countryCode ?? 'rs',
          city: dataToSend.city ?? '',
          address: dataToSend.address ?? '',
          contactStatus: dataToSend.contactStatus ?? 'nije_kontaktiran',
          interestLevel: dataToSend.interestLevel ?? '',
          lastContactedAt: dataToSend.lastContactedAt || undefined,
          contactNotes: dataToSend.contactNotes ?? '',
          contactedVia: Array.isArray(dataToSend.contactedVia) ? dataToSend.contactedVia : []
        };
        const response = await updateBusinessContact({
          id: editingContact._id,
          contactData: updatePayload
        });
        console.log('📥 Updated contact response:', response.data);
        replaceContactInLists(response.data);
        setFocusContactId(editingContact._id);
        toast.success('Kontakt je uspešno ažuriran');
        resetForm();
      } else {
        await createBusinessContact(dataToSend);
        toast.success('Kontakt je uspešno dodat');
        resetForm();
        setCurrentPage(1);
        await fetchAllContacts();
      }
    } catch (error) {
      console.error('Error saving business contact:', error);
      toast.error(error.response?.data?.message || 'Greška pri čuvanju kontakta');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Da li ste sigurni da želite da obrišete ovaj kontakt?')) {
      return;
    }

    try {
      await deleteBusinessContact(id);
      toast.success('Kontakt je uspešno obrisan');
      removeContactFromLists(id);
    } catch (error) {
      console.error('Error deleting business contact:', error);
      toast.error('Greška pri brisanju kontakta');
    }
  };

  const handleAiExtraction = async () => {
    if (!aiText.trim()) {
      toast.error('Molimo unesite tekst za ekstrakciju');
      return;
    }

    setAiLoading(true);
    try {
      const response = await extractBusinessContactDataFromText(aiText.trim());
      
      if (response.data && response.data.success && response.data.data) {
        const extractedData = response.data.data;
        
        // Detect country code from extracted country name or phone numbers
        let detectedCountryCode = 'rs';
        if (extractedData.country) {
          // Try to match country name to country code
          const countryLower = extractedData.country.toLowerCase();
          const matchedCountry = Object.entries(COUNTRY_DATA).find(
            ([code, data]) => data.name.toLowerCase() === countryLower
          );
          if (matchedCountry) {
            detectedCountryCode = matchedCountry[0];
          }
        }
        // If country code not found, try to detect from phone numbers
        if (detectedCountryCode === 'rs' && (extractedData.mobilePhone || extractedData.fixedPhone)) {
          detectedCountryCode = detectCountryCodeFromPhone(extractedData.mobilePhone || extractedData.fixedPhone);
        }

        // Populate form with extracted data
        setFormData(prev => ({
          ...prev,
          businessType: extractedData.businessType || prev.businessType || '',
          companyName: extractedData.companyName || prev.companyName || '',
          mobilePhone: extractedData.mobilePhone || prev.mobilePhone || '',
          fixedPhone: extractedData.fixedPhone || prev.fixedPhone || '',
          email: extractedData.email || prev.email || '',
          country: extractedData.country || prev.country || '',
          countryCode: detectedCountryCode,
          city: extractedData.city || prev.city || '',
          address: extractedData.address || prev.address || ''
        }));
        
        setShowAiModal(false);
        setAiText('');
        setShowForm(true);
        toast.success('Podaci su uspešno izvučeni! Pregledajte i sačuvajte kontakt.');
      } else {
        toast.error('Nije moguće izvući podatke iz teksta');
      }
    } catch (error) {
      console.error('Error extracting business contact data:', error);
      toast.error(error.response?.data?.message || 'Greška pri ekstrakciji podataka');
    } finally {
      setAiLoading(false);
    }
  };

  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          // Escaped quote
          current += '"';
          i++;
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        // End of field
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    // Add last field
    result.push(current.trim());
    return result;
  };

  const handleImportCSV = async () => {
    if (!importFile) {
      toast.error('Izaberite CSV fajl za uvoz');
      return;
    }

    try {
      setImporting(true);
      
      // Read file and parse CSV
      const text = await importFile.text();
      const lines = text.split(/\r?\n/).filter(line => line.trim());
      
      if (lines.length < 2) {
        toast.error('CSV fajl je prazan ili nema podataka');
        return;
      }

      // Parse CSV headers (handle quoted values)
      const headers = parseCSVLine(lines[0]).map(h => h.replace(/^"|"$/g, ''));
      const contacts = [];

      // Process data rows
      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue; // Skip empty lines
        
        const values = parseCSVLine(lines[i]).map(v => v.replace(/^"|"$/g, ''));
        const contact = {};
        
        headers.forEach((header, index) => {
          contact[header] = values[index] || '';
        });
        
        // Only add if has at least phone or company name
        if (contact['Phone'] || contact['Company'] || contact['Ime Kompanije']) {
          contacts.push(contact);
        }
      }

      if (contacts.length === 0) {
        toast.error('Nema validnih kontakata u CSV fajlu. Proverite format.');
        return;
      }

      // Send to backend
      const response = await importBusinessContactsFromCSV(contacts);
      
      if (response.data.results.errors && response.data.results.errors.length > 0) {
        toast.warning(`${response.data.results.imported} kontakata uvezeno, ${response.data.results.skipped} preskočeno (${response.data.results.errors.length} grešaka)`);
      } else {
        toast.success(`${response.data.results.imported} kontakata uvezeno, ${response.data.results.skipped} preskočeno`);
      }
      
      // Refresh contacts list
      await fetchAllContacts();
      
      // Close modal and reset
      setShowImportModal(false);
      setImportFile(null);
    } catch (error) {
      console.error('Error importing contacts:', error);
      toast.error(error.response?.data?.message || 'Greška pri uvozu kontakata');
    } finally {
      setImporting(false);
    }
  };

  const contactsForFilters = allContacts.length > 0 ? allContacts : contacts;
  const uniqueCountries = [...new Set(contactsForFilters.map(c => c.country).filter(Boolean))].sort((a, b) => 
    a.localeCompare(b, 'sr', { sensitivity: 'base' })
  );
  const uniqueCities = [...new Set(contactsForFilters.map(c => c.city).filter(Boolean))].sort((a, b) => 
    a.localeCompare(b, 'sr', { sensitivity: 'base' })
  );

  const emailOnlyActive = filterHasEmail || filterCountry === 'samo-mejlovi';

  const filteredContacts = allContacts.filter((contact) => {
    // Search term filter - search in company name, business type, email, phones, city, country, and address
    const matchesSearch = !searchTerm || 
      contact.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.businessType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.mobilePhone?.includes(searchTerm) ||
      contact.fixedPhone?.includes(searchTerm) ||
      contact.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.country?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.address?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesEmailOnly = !emailOnlyActive || !!(contact.email && contact.email.trim() !== '');

    let matchesCountry = true;
    if (filterCountry && filterCountry !== 'samo-mejlovi') {
      matchesCountry = contact.country === filterCountry;
    }

    const matchesCity = !filterCity || contact.city === filterCity;

    const matchesContactStatus =
      !filterContactStatus || (contact.contactStatus || 'nije_kontaktiran') === filterContactStatus;

    const matchesScore = contactMatchesScoreFilter(contact);

    return (
      matchesSearch &&
      matchesEmailOnly &&
      matchesCountry &&
      matchesCity &&
      matchesContactStatus &&
      matchesScore
    );
  });

  const contactsPerPage = 10;

  const gmailUrlForContact = (contact, templateKey = 'empty') => {
    const email = (contact?.email || '').trim();
    if (!email) return '#';
    if (templateKey === 'empty') {
      return buildGmailComposeUrl(email, '', '');
    }
    const packs = buildOutreachTemplates({
      companyName: contact.companyName || '',
      contactEmail: email
    });
    const pack = packs[templateKey];
    if (!pack) return buildGmailComposeUrl(email, '', '');
    return buildGmailComposeUrl(email, pack.subject, pack.body);
  };

  const paginationTotalPages = Math.max(1, Math.ceil(filteredContacts.length / contactsPerPage));
  const effectivePage = Math.min(currentPage, paginationTotalPages);

  useEffect(() => {
    setPageInput(String(effectivePage));
  }, [effectivePage]);

  const jumpToPage = () => {
    const parsed = Number.parseInt(pageInput, 10);
    if (!Number.isFinite(parsed)) {
      setPageInput(String(effectivePage));
      return;
    }
    const next = Math.min(paginationTotalPages, Math.max(1, parsed));
    setCurrentPage(next);
    setPageInput(String(next));
  };

  const paginationStartIndex = (effectivePage - 1) * contactsPerPage;
  const paginatedFilteredContacts = filteredContacts.slice(
    paginationStartIndex,
    paginationStartIndex + contactsPerPage
  );

  useEffect(() => {
    if (!focusContactId) return;
    const rows = document.querySelectorAll(`[data-contact-id="${focusContactId}"]`);
    const row = Array.from(rows).find((el) => el.offsetParent !== null);
    if (row) {
      row.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
    setFocusContactId(null);
  }, [focusContactId]);

  const handleExportCSV = () => {
    if (filteredContacts.length === 0) {
      toast.error('Nema kontakata za eksport sa trenutnim filterima');
      return;
    }

    try {
      const csvContent = buildBusinessContactsCsv(filteredContacts);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `spinTasker-kontakti-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      const countLabel = filteredContacts.length === 1 ? 'kontakt' : 'kontakata';
      toast.success(`Eksportovano ${filteredContacts.length} ${countLabel} u CSV fajl`);
    } catch (error) {
      console.error('Error exporting contacts:', error);
      toast.error('Greška pri eksportovanju kontakata');
    }
  };

  if (!isSuperAdmin) {
    return null;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Poslovanje</h1>
      </div>

      {/* First Row: Statistics, Search, Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-4 items-start sm:items-center">
        {/* Statistics */}
        <div className="bg-gray-800 rounded-lg px-4 py-2 border border-gray-700 flex items-center gap-2">
          <Building2 size={18} className="text-blue-500" />
          <div className="text-gray-300 text-sm">
            <span className="font-medium text-white">{totalContacts}</span> {totalContacts === 1 ? 'kompanija' : 'kompanija'}
            {filteredContacts.length !== totalContacts && (
              <span className="ml-2 text-gray-400">
                ({filteredContacts.length} {filteredContacts.length === 1 ? 'prikazana' : 'prikazane'})
              </span>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="flex-1 min-w-0 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Pretraži kontakte..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Filters */}
        <select
          value={filterCountry}
          onChange={(e) => {
            const val = e.target.value;
            setFilterCountry(val);
            if (val === 'samo-mejlovi') setFilterHasEmail(true);
          }}
          className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[160px] w-full sm:w-auto"
        >
          <option value="">Sve države</option>
          {uniqueCountries.map(country => (
            <option key={country} value={country}>{country}</option>
          ))}
          <option value="samo-mejlovi">Samo mejlovi</option>
        </select>
        <select
          value={filterCity}
          onChange={(e) => setFilterCity(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[160px] w-full sm:w-auto"
        >
          <option value="">Svi gradovi</option>
          {uniqueCities.map(city => (
            <option key={city} value={city}>{city}</option>
          ))}
        </select>
        <label className="flex items-center gap-2 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm whitespace-nowrap cursor-pointer">
          <input
            type="checkbox"
            checked={filterHasEmail || filterCountry === 'samo-mejlovi'}
            onChange={(e) => {
              setFilterHasEmail(e.target.checked);
              if (!e.target.checked && filterCountry === 'samo-mejlovi') {
                setFilterCountry('');
              }
            }}
            className="rounded border-gray-500"
          />
          Samo sa mejlom
        </label>
        <select
          value={filterContactStatus}
          onChange={(e) => setFilterContactStatus(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[180px] w-full sm:w-auto"
        >
          <option value="">Status kontakta</option>
          {Object.entries(CONTACT_STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <select
          value={filterScoreChannel}
          onChange={(e) => setFilterScoreChannel(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[140px] w-full sm:w-auto"
        >
          <option value="">Kanal ocene</option>
          {Object.entries(SCORE_CHANNEL_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <select
          value={filterMinScore}
          onChange={(e) => setFilterMinScore(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[130px] w-full sm:w-auto"
        >
          <option value="">Ocena min.</option>
          {[1, 2, 3, 4, 5].map((n) => (
            <option key={n} value={String(n)}>{n}+</option>
          ))}
        </select>
      </div>

      {/* Second Row: Action Buttons */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
        >
          <Plus size={18} />
          Dodaj kontakt
        </button>
        <button
          onClick={handleExportCSV}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap"
        >
          <Download size={18} />
          Export CSV
        </button>
        <button
          onClick={() => setShowImportModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors whitespace-nowrap"
        >
          <Upload size={18} />
          Import CSV
        </button>
        <button
          onClick={() => setShowAiModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors whitespace-nowrap"
        >
          <Sparkles size={18} />
          AI Ekstrakcija
        </button>
        {hasActiveFilters() && (
          <button
            onClick={() => {
              setFilterCountry('');
              setFilterCity('');
              setFilterContactStatus('');
              setFilterHasEmail(false);
              setFilterScoreChannel('');
              setFilterMinScore('');
              setSearchTerm('');
              setCurrentPage(1);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors whitespace-nowrap"
          >
            <X size={18} />
            Obriši filtere
          </button>
        )}
      </div>

      {/* AI Extraction Modal */}
      {showAiModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-gray-800 rounded-lg shadow-lg w-full max-w-2xl">
            <div className="border-b border-gray-700 p-4 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Sparkles className="text-purple-500" size={20} />
                <h2 className="text-lg font-semibold text-white">AI Ekstrakcija podataka</h2>
              </div>
              <button
                onClick={() => {
                  setShowAiModal(false);
                  setAiText('');
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4">
              <p className="text-sm text-gray-400 mb-4">
                Nalepite tekst koji sadrži informacije o kompaniji (ime, mobilni i/ili fiksni telefon, email, adresa, grad, država). 
                AI će automatski izvući podatke i pravilno razlikovati mobilni (06X) i fiksni (01X, 02X, 03X) telefon.
              </p>
              
              <textarea
                value={aiText}
                onChange={(e) => setAiText(e.target.value)}
                placeholder="Primer: Kompanija Tech Solutions d.o.o., Beograd, Bulevar kralja Aleksandra 15, telefon 0651234567, email info@techsolutions.rs"
                rows="8"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
              />
              
              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={() => {
                    setShowAiModal(false);
                    setAiText('');
                  }}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Otkaži
                </button>
                <button
                  onClick={handleAiExtraction}
                  disabled={aiLoading || !aiText.trim()}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {aiLoading && <Loader2 size={16} className="animate-spin" />}
                  {aiLoading ? 'Ekstrahujem...' : 'Ekstrahuj podatke'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Zabeleži kontakt / evidencija */}
      {showLogContactModal && logContactTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-gray-800 rounded-lg shadow-lg w-full max-w-lg">
            <div className="border-b border-gray-700 p-4 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <ClipboardList className="text-amber-500" size={20} />
                <h2 className="text-lg font-semibold text-white">Evidencija kontakta – {logContactTarget.companyName}</h2>
              </div>
              <button
                onClick={() => { setShowLogContactModal(false); setLogContactTarget(null); }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-purple-600 border-2 border-purple-500 text-white text-xs font-bold">V</span> Viber ocena (0–5)
                  </label>
                  <select
                    value={logContactForm.viberScore}
                    onChange={(e) => setLogContactForm(prev => ({ ...prev, viberScore: e.target.value }))}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">– Nije kontaktiran</option>
                    {[1, 2, 3, 4, 5].map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-white border-2 border-green-500 text-green-600 text-xs font-bold">W</span> WhatsApp ocena (0–5)
                  </label>
                  <select
                    value={logContactForm.whatsappScore}
                    onChange={(e) => setLogContactForm(prev => ({ ...prev, whatsappScore: e.target.value }))}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">– Nije kontaktiran</option>
                    {[1, 2, 3, 4, 5].map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-black border-2 border-gray-800 text-white text-xs font-bold">E</span> Mejl ocena (0–5)
                  </label>
                  <select
                    value={logContactForm.emailScore}
                    onChange={(e) => setLogContactForm(prev => ({ ...prev, emailScore: e.target.value }))}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-gray-600"
                  >
                    <option value="">– Nije kontaktiran</option>
                    {[1, 2, 3, 4, 5].map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-orange-500 border-2 border-orange-600 text-white text-xs font-bold">T</span> Telefon ocena (0–5)
                  </label>
                  <select
                    value={logContactForm.telefonScore}
                    onChange={(e) => setLogContactForm(prev => ({ ...prev, telefonScore: e.target.value }))}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">– Nije kontaktiran</option>
                    {[1, 2, 3, 4, 5].map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-red-600 border-2 border-red-500 text-white text-xs font-bold">P</span> Nivo paketa
                </label>
                <select
                  value={logContactForm.packageLevel}
                  onChange={(e) => setLogContactForm(prev => ({ ...prev, packageLevel: e.target.value }))}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="">– Nije izabran</option>
                  {Object.entries(PACKAGE_LEVEL_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{value} – {label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Datum kontakta</label>
                <input
                  type="date"
                  value={logContactForm.lastContactedAt}
                  onChange={(e) => setLogContactForm(prev => ({ ...prev, lastContactedAt: e.target.value }))}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Beleška (reakcija, sledeći korak…)</label>
                <textarea
                  value={logContactForm.contactNotes}
                  onChange={(e) => setLogContactForm(prev => ({ ...prev, contactNotes: e.target.value }))}
                  rows={3}
                  placeholder="npr. Zainteresovani, žele demo u petak..."
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => { setShowLogContactModal(false); setLogContactTarget(null); }}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Otkaži
                </button>
                <button
                  onClick={handleSaveLogContact}
                  disabled={savingLog}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {savingLog && <Loader2 size={16} className="animate-spin" />}
                  Sačuvaj evidenciju
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="bg-gray-800 rounded-lg shadow-lg mb-6">
          <div className="border-b border-gray-700 p-4 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-white">
              {editingContact ? 'Izmeni kontakt' : 'Dodaj novi kontakt'}
            </h2>
            <button
              onClick={resetForm}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Delatnost
                </label>
                <input
                  type="text"
                  name="businessType"
                  value={formData.businessType}
                  onChange={handleInputChange}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Delatnost (npr. Servis bele tehnike)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Ime kompanije *
                </label>
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  required
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ime kompanije (npr. Aca)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Mobilni telefon
                </label>
                <input
                  type="tel"
                  name="mobilePhone"
                  value={formData.mobilePhone}
                  onChange={handleInputChange}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Mobilni (npr. 0651234567)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Fiksni telefon
                </label>
                <input
                  type="tel"
                  name="fixedPhone"
                  value={formData.fixedPhone}
                  onChange={handleInputChange}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Fiksni (npr. 011/123-4567)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Email
                </label>
                <EmailProviderInput
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Pozivni broj države (za telefone)
                </label>
                <select
                  name="countryCode"
                  value={formData.countryCode}
                  onChange={handleInputChange}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {Object.entries(COUNTRY_DATA)
                    .sort((a, b) => a[1].name.localeCompare(b[1].name))
                    .map(([code, data]) => (
                      <option key={code} value={code}>
                        {data.name} (+{data.code})
                      </option>
                    ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">
                  Telefoni će biti automatski konvertovani u E.164 format (+{COUNTRY_DATA[formData.countryCode]?.code || '381'}...)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Grad
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Grad"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Adresa
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Adresa"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-700">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Otkaži
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {submitting && <Loader2 size={16} className="animate-spin" />}
                {editingContact ? 'Sačuvaj izmene' : 'Dodaj kontakt'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Contacts List */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 size={32} className="animate-spin text-blue-500" />
        </div>
      ) : paginatedFilteredContacts.length === 0 ? (
        <div className="bg-gray-800 rounded-lg p-8 text-center">
          <Building2 size={48} className="mx-auto text-gray-600 mb-4" />
          <p className="text-gray-400">
            {searchTerm ? 'Nema rezultata pretrage' : 'Nema dodatih kontakata'}
          </p>
        </div>
      ) : (
        <>
          {/* Desktop: Horizontal Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full bg-gray-800 rounded-lg border border-gray-700" style={{ fontSize: '0.8rem' }}>
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="px-3 py-2 text-left font-semibold text-gray-300" style={{ fontSize: '0.8rem' }}>Delatnost</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-300" style={{ fontSize: '0.8rem' }}>Kompanija</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-300" style={{ fontSize: '0.8rem' }}>Status</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-300" style={{ fontSize: '0.8rem' }}>Mobilni</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-300" style={{ fontSize: '0.8rem' }}>Fiksni</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-300" style={{ fontSize: '0.8rem' }}>Email</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-300" style={{ fontSize: '0.8rem' }}>Država</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-300" style={{ fontSize: '0.8rem' }}>Grad</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-300" style={{ fontSize: '0.8rem' }}>Adresa</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-300" style={{ fontSize: '0.8rem' }}>Akcije</th>
                </tr>
              </thead>
              <tbody>
                {paginatedFilteredContacts.map((contact) => {
                  // Auto-detect country from phone prefix if not set
                  let displayCountry = contact.country;
                  if (!displayCountry && (contact.mobilePhone || contact.fixedPhone)) {
                    const phoneToCheck = contact.mobilePhone || contact.fixedPhone;
                    if (phoneToCheck.startsWith('+381')) displayCountry = 'Serbia';
                    else if (phoneToCheck.startsWith('+387')) displayCountry = 'Bosnia and Herzegovina';
                    else if (phoneToCheck.startsWith('+385')) displayCountry = 'Croatia';
                    else if (phoneToCheck.startsWith('+382')) displayCountry = 'Montenegro';
                    else if (phoneToCheck.startsWith('+389')) displayCountry = 'North Macedonia';
                    else if (phoneToCheck.startsWith('+386')) displayCountry = 'Slovenia';
                    else if (phoneToCheck.startsWith('+966')) displayCountry = 'Saudi Arabia';
                  }
                  
                  return (
                    <tr
                      key={contact._id}
                      data-contact-id={contact._id}
                      className="border-b border-gray-700 hover:bg-gray-750 transition-colors"
                    >
                      <td className="px-3 py-2 text-gray-300" style={{ fontSize: '0.8rem' }}>{contact.businessType || '-'}</td>
                      <td className="px-3 py-2 text-white font-medium" style={{ fontSize: '0.8rem' }}>{contact.companyName}</td>
                      <td className="px-3 py-2" style={{ fontSize: '0.8rem' }}>
                        {renderStatusBadges(contact)}
                      </td>
                      <td className="px-3 py-2 text-gray-300" style={{ fontSize: '0.8rem' }}>{contact.mobilePhone || '-'}</td>
                      <td className="px-3 py-2 text-gray-300" style={{ fontSize: '0.8rem' }}>{contact.fixedPhone || '-'}</td>
                      <td className="px-3 py-2 text-gray-300" style={{ fontSize: '0.8rem' }}>
                        {contact.email?.trim() ? (
                          <a
                            href={gmailUrlForContact(contact, 'empty')}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sky-400 hover:text-sky-300 underline break-all"
                            title="Otvori Gmail (compose)"
                          >
                            {contact.email}
                          </a>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-300" style={{ fontSize: '0.8rem' }}>{displayCountry || '-'}</td>
                      <td className="px-3 py-2 text-gray-300" style={{ fontSize: '0.8rem' }}>{contact.city || '-'}</td>
                      <td className="px-3 py-2 text-gray-300" style={{ fontSize: '0.8rem' }}>{contact.address || '-'}</td>
                      <td className="px-3 py-2">
                        <div className="flex justify-center gap-2">
                          {contact.email?.trim() && (
                            <a
                              href={gmailUrlForContact(contact, 'promo')}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sky-400 hover:text-sky-300 transition-colors"
                              title="Gmail sa promo šablonom"
                            >
                              <Mail size={14} />
                            </a>
                          )}
                          <button
                            onClick={() => openLogContactModal(contact)}
                            className="text-gray-400 hover:text-amber-500 transition-colors"
                            title="Zabeleži kontakt / evidencija"
                          >
                            <ClipboardList size={14} />
                          </button>
                          <button
                            onClick={() => handleEdit(contact)}
                            className="text-gray-400 hover:text-blue-500 transition-colors"
                            title="Izmeni"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            onClick={() => handleDelete(contact._id)}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                            title="Obriši"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile: Card View */}
          <div className="md:hidden grid grid-cols-1 gap-4">
            {paginatedFilteredContacts.map((contact) => (
              <div
                key={contact._id}
                data-contact-id={contact._id}
                className="bg-gray-800 rounded-lg p-4 border border-gray-700 hover:border-blue-500 transition-colors"
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Building2 size={20} className="text-blue-500" />
                    <h3 className="font-semibold text-white">{contact.companyName}</h3>
                    {renderStatusBadges(contact, true)}
                  </div>
                  <div className="flex gap-2 items-center">
                    {contact.email?.trim() && (
                      <a
                        href={gmailUrlForContact(contact, 'promo')}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sky-400 hover:text-sky-300 p-1"
                        title="Gmail sa promo šablonom"
                      >
                        <Mail size={16} />
                      </a>
                    )}
                    <button
                      onClick={() => openLogContactModal(contact)}
                      className="text-gray-400 hover:text-amber-500 transition-colors"
                      title="Zabeleži kontakt"
                    >
                      <ClipboardList size={16} />
                    </button>
                    <button
                      onClick={() => handleEdit(contact)}
                      className="text-gray-400 hover:text-blue-500 transition-colors"
                      title="Izmeni"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(contact._id)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      title="Obriši"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  {contact.mobilePhone && (
                    <div className="flex items-center gap-2 text-gray-300">
                      <Phone size={14} className="text-gray-500" />
                      <span className="text-xs text-gray-400">Mob:</span>
                      <span>{contact.mobilePhone}</span>
                    </div>
                  )}
                  {contact.fixedPhone && (
                    <div className="flex items-center gap-2 text-gray-300">
                      <Phone size={14} className="text-gray-500" />
                      <span className="text-xs text-gray-400">Fiks:</span>
                      <span>{contact.fixedPhone}</span>
                    </div>
                  )}
                  {contact.email?.trim() && (
                    <div className="flex items-center gap-2 text-gray-300 flex-wrap">
                      <Mail size={14} className="text-gray-500 shrink-0" />
                      <a
                        href={gmailUrlForContact(contact, 'empty')}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sky-400 hover:text-sky-300 underline break-all text-xs"
                      >
                        {contact.email}
                      </a>
                      <a
                        href={gmailUrlForContact(contact, 'promo')}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-purple-400 hover:text-purple-300 shrink-0"
                        title="Gmail sa promo šablonom"
                      >
                        (promo)
                      </a>
                    </div>
                  )}
                  {(contact.city || contact.country) && (
                    <div className="flex items-center gap-2 text-gray-300">
                      <MapPin size={14} className="text-gray-500" />
                      <span>
                        {[contact.city, contact.country].filter(Boolean).join(', ')}
                      </span>
                    </div>
                  )}
                  {contact.address && (
                    <div className="text-gray-400 text-xs mt-2">
                      {contact.address}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Pagination */}
      {!loading && paginationTotalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={effectivePage === 1}
            className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <ChevronLeft size={18} />
            Prethodna
          </button>
          
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, paginationTotalPages) }, (_, i) => {
              const totalPagesToShow = paginationTotalPages;
              let pageNum;
              if (totalPagesToShow <= 5) {
                pageNum = i + 1;
              } else if (effectivePage <= 3) {
                pageNum = i + 1;
              } else if (effectivePage >= totalPagesToShow - 2) {
                pageNum = totalPagesToShow - 4 + i;
              } else {
                pageNum = effectivePage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-3 py-2 rounded-lg transition-colors ${
                    effectivePage === pageNum
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-800 border border-gray-700 text-white hover:bg-gray-700'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(paginationTotalPages, prev + 1))}
            disabled={effectivePage === paginationTotalPages}
            className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            Sledeća
            <ChevronRight size={18} />
          </button>
        </div>
      )}
      
      {!loading && paginationTotalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-2 text-gray-400 text-sm flex-wrap">
          <span>Stranica {effectivePage} od {paginationTotalPages}</span>
          <form
            className="inline-flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              jumpToPage();
            }}
          >
            <label htmlFor="business-contacts-page-jump" className="sr-only">
              Idi na stranicu
            </label>
            <input
              id="business-contacts-page-jump"
              type="number"
              min={1}
              max={paginationTotalPages}
              inputMode="numeric"
              value={pageInput}
              onChange={(e) => setPageInput(e.target.value)}
              className="w-16 bg-gray-800 border border-gray-700 rounded-lg px-2 py-1 text-white text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="submit"
              className="px-3 py-1 bg-gray-800 border border-gray-700 rounded-lg text-white hover:bg-gray-700 transition-colors"
            >
              Idi
            </button>
          </form>
          {hasActiveFilters() && (
            <span>
              ({filteredContacts.length} {filteredContacts.length === 1 ? 'rezultat' : 'rezultata'})
            </span>
          )}
        </div>
      )}

      {/* Import CSV Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-gray-800 rounded-lg shadow-lg w-full max-w-2xl">
            <div className="border-b border-gray-700 p-4 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Upload className="text-yellow-500" size={20} />
                <h2 className="text-lg font-semibold text-white">Import Kontakata iz CSV</h2>
              </div>
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setImportFile(null);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4">
              <p className="text-sm text-gray-400 mb-4">
                Izaberite CSV fajl sa kontaktima. Format: <code className="bg-gray-700 px-2 py-1 rounded">First Name, Last Name, Phone, Email, Company</code>
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  CSV Fajl
                </label>
                <input
                  type="file"
                  accept=".csv,.txt"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700"
                />
                {importFile && (
                  <p className="text-sm text-gray-400 mt-2">
                    Izabran fajl: {importFile.name}
                  </p>
                )}
              </div>

              <div className="bg-gray-700 rounded-lg p-3 mb-4">
                <p className="text-xs text-gray-400 mb-2">
                  <strong>Primer CSV formata:</strong>
                </p>
                <code className="text-xs text-gray-300 block whitespace-pre-wrap">
{`First Name,Last Name,Phone,Email,Company
Marko,Petrović,+381651234567,marko@example.com,Marko Servis
Jovan,Jovanović,+381641112233,jovan@example.com,Jovan Auto`}
                </code>
              </div>
              
              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setImportFile(null);
                  }}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Otkaži
                </button>
                <button
                  onClick={handleImportCSV}
                  disabled={importing || !importFile}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {importing && <Loader2 size={16} className="animate-spin" />}
                  {importing ? 'Uvozim...' : 'Uvezi kontakte'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessContactsView;

